/**
 * The app setup's key step (Mode B — installing, or a setup that reopens when the app's env.json changes): each of the
 * app's secrets this person can't simply allow — no key named, a key they can't open (or not in this org), or no field —
 * with a picker of THEIR keys. Choosing one saves the app's variable (its keyId/field in env.json, and the app's grant)
 * through the same writer as Variables and secrets; a secret left unchosen stays for Settings.
 *
 * Display: the same look as Variables and secrets (a bordered list, the name in mono with the Secret badge, the reason
 * in the warning colour, the picker opening in place under its row, a chosen key shown as its key chip).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, Check, KeyRound, Loader2, X } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { keybookApi } from '../client/api.js'
import { KeyFieldPicker, type KeyFieldChoice } from './KeyFieldPicker.js'
import { KeyChip, SecretBadge } from './KeyChip.js'
import { errorText } from '@papan/api-client'

export interface AppKeyPick {
  /** The setup item's hash (so the host can drop it once chosen). */
  contentHash: string
  name: string
  description: string | null
  needs: 'no-key' | 'unavailable' | 'no-field'
}

/** Display only: how a chosen key is shown (its chip) once its save is in. `label` null: a key not in the person's list. */
export interface ChosenKeyView { label: string | null; credentialType: string; field: string }

/** A key's field the row names only when it isn't this one (as Variables and secrets). */
const DEFAULT_FIELD = 'key'

function ChosenKey({ view }: { view: ChosenKeyView }) {
  const { t } = useTranslation('keybook')
  const field = view.field && view.field !== DEFAULT_FIELD ? view.field : null
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <KeyChip label={view.label ?? t('appEnv.aKey')} credentialType={view.credentialType} title={`${view.label ?? ''} · ${view.field}`} />
      {field && <span className="themed-text-muted font-mono shrink-0">{field}</span>}
    </span>
  )
}

export function AppKeyPicks({ folderId, items, chosen, onChosen, onBusy, disabled, framed = true, extraAction, chosenKeys, onUse }: {
  folderId: string
  items: AppKeyPick[]
  /** Hashes already chosen in this setup (shown as done). */
  chosen: ReadonlySet<string>
  /** Captured when a save STARTS: a save finishing after the host moved to another app reports to the one it began in. */
  onChosen: (contentHash: string) => void
  /** A save starts (true) / ends (false) — captured when it starts, like `onChosen`; the host COUNTS them and holds its
   *  own Continue / Back while any is in flight (a set-aside sent meanwhile would race it). */
  onBusy?: (busy: boolean) => void
  disabled?: boolean
  /** Display only. Off: no border of its own — the host's row is the frame (the review dialog). */
  framed?: boolean
  /** Display only: a host action beside "Choose a key" on a row not chosen yet (the review dialog's Set aside). */
  extraAction?: (item: AppKeyPick) => ReactNode
  /** Display only: chosen keys the host remembers (this list can remount, e.g. Back and forward again). */
  chosenKeys?: ReadonlyMap<string, ChosenKeyView>
  /** Display only: "Use this key" was pressed for this row with this key — the host may remember it for its review. */
  onUse?: (contentHash: string, key: ChosenKeyView) => void
}) {
  const { t } = useTranslation('keybook')
  const [open, setOpen] = useState<string | null>(null)
  const [choice, setChoice] = useState<KeyFieldChoice>({ entryId: null, field: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async (item: AppKeyPick) => {
    if (!choice.entryId || !choice.field) return
    const { entryId, field } = choice
    const report = onChosen
    const busyReport = onBusy
    setBusy(true); busyReport?.(true); setError(null)
    // A save whose answer was lost may still have landed (as Variables and secrets checks): the app's variable naming
    // this key and field, connected.
    const landed = async () => {
      try {
        const r = await keybookApi.getAppEnv(folderId)
        const v = r.vars.find(x => x.name === item.name)
        return !!v?.secret && v.secret.status === 'ok' && v.secret.entryId === entryId && v.secret.field === field
      } catch { return false }
    }
    try {
      await keybookApi.setAppEnv(folderId, item.name, { kind: 'secret', entryId, field, ...(item.description ? { description: item.description } : {}) })
      report(item.contentHash)
      setOpen(null); setChoice({ entryId: null, field: '' })
    } catch (e) {
      if (await landed()) { report(item.contentHash); setOpen(null); setChoice({ entryId: null, field: '' }) }
      else { const text = errorText(e); if (text !== null) setError(text) }   // #1316: a dropped answer shows nothing
    } finally {
      setBusy(false); busyReport?.(false)
    }
  }

  // Display only: how the open row's picked key is shown (from the picker), and the keys this list saw saved.
  const [pickedKey, setPickedKey] = useState<{ label: string; credentialType: string } | null>(null)
  const [usedKeys, setUsedKeys] = useState<ReadonlyMap<string, ChosenKeyView>>(new Map())

  const listRef = useRef<HTMLUListElement>(null)
  const focusNext = useRef<{ kind: 'picker' | 'choose' | 'after-use'; hash: string } | null>(null)

  // Display only: a secret whose key is named but not its field opens on that key, as Settings' editor does — the app's
  // variables say which key (read when the row opens; the picker waits for it, so its list never swaps under a click).
  // Nothing is saved until Use; Change picks another. A failed read, or a secret that changed meanwhile, opens empty.
  const [prefilling, setPrefilling] = useState<string | null>(null)
  // The named key's name, for its card while it isn't in the person's list — that key only (as Settings' editor).
  const [prefillFallback, setPrefillFallback] = useState<{ entryId: string; label: string; credentialType: string | null } | undefined>(undefined)
  const prefillSeq = useRef(0)
  useEffect(() => () => { prefillSeq.current++ }, [])
  const openRow = (item: AppKeyPick) => {
    setOpen(item.contentHash); setChoice({ entryId: null, field: '' }); setError(null); setPickedKey(null); setPrefillFallback(undefined)
    focusNext.current = { kind: 'picker', hash: item.contentHash }
    const mine = ++prefillSeq.current
    if (item.needs !== 'no-field') { setPrefilling(null); return }
    setPrefilling(item.contentHash)
    void Promise.resolve()
      .then(() => keybookApi.getAppEnv(folderId))
      .then(r => {
        if (mine !== prefillSeq.current) return
        const s = r.vars.find(x => x.name === item.name)?.secret
        if (s?.entryId && s.needs === 'no-field') {
          setChoice({ entryId: s.entryId, field: '' })
          if (s.label) setPrefillFallback({ entryId: s.entryId, label: s.label, credentialType: s.credentialType })
        }
      })
      .catch(() => {})
      .finally(() => { if (mine === prefillSeq.current) setPrefilling(null) })
  }
  const closeRow = (item: AppKeyPick) => {
    prefillSeq.current++; setPrefilling(null)
    setOpen(null); focusNext.current = { kind: 'choose', hash: item.contentHash }
  }

  // Focus follows what replaced the button pressed: Choose opens the picker (its search box), Cancel gives focus back to
  // Choose, and a saved row hands it to the next row still to choose (else the saved row itself).
  useEffect(() => {
    const want = focusNext.current
    const root = listRef.current
    if (!want || !root || busy) return
    const row = [...root.querySelectorAll<HTMLElement>('[data-pick]')].find(el => el.dataset.pick === want.hash)
    // Still reading which key the secret names: the picker isn't shown yet.
    if (want.kind === 'picker' && prefilling === want.hash) return
    focusNext.current = null
    // The key search, or — opened on the named key — its card's Change.
    if (want.kind === 'picker') { (row?.querySelector<HTMLElement>('[data-testid="pdapp-setup-key-filter"]') ?? row?.querySelector<HTMLElement>('[data-testid="pdapp-setup-change-key"]'))?.focus(); return }
    if (want.kind === 'choose') { row?.querySelector<HTMLElement>('[data-testid="pdapp-setup-key-choose"]')?.focus(); return }
    // After Use: still open = the save failed (its error shows, Use is there again) — focus stays where it is.
    if (open === want.hash) return
    const all = [...root.querySelectorAll<HTMLButtonElement>('[data-testid="pdapp-setup-key-choose"]')].filter(b => !b.disabled && !row?.contains(b))
    const after = row ? all.find(b => row.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) : undefined
    const next = after ?? all[0]
    if (next) next.focus()
    else row?.focus()
  })

  const reason = (needs: AppKeyPick['needs']) =>
    needs === 'unavailable' ? t('appEnv.needsUnavailable') : needs === 'no-field' ? t('appEnv.needsNoField') : t('appEnv.needsNoKey')

  return (
    <ul ref={listRef} className={framed ? 'rounded-lg border themed-border overflow-hidden' : ''} data-testid="pdapp-setup-key-picks">
      {items.map(item => {
        const done = chosen.has(item.contentHash)
        const isOpen = open === item.contentHash
        const shownKey = chosenKeys?.get(item.contentHash) ?? usedKeys.get(item.contentHash)
        return (
          <li
            key={item.contentHash}
            data-pick={item.contentHash}
            tabIndex={-1}
            className={`outline-none ${framed ? 'px-3 py-2.5 border-b themed-border last:border-b-0' : 'px-3 py-2'}`}
            data-testid="pdapp-setup-key-pick"
            data-name={item.name}
          >
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <code className="text-xs font-mono font-medium themed-text truncate" title={item.name}>{item.name}</code>
                  <SecretBadge label={t('appEnv.kindSecret')} />
                </div>
                {item.description && <p className="text-xs themed-text-muted truncate mt-0.5" title={item.description}>{item.description}</p>}
                <div className="mt-1 text-xs">
                  {done ? (
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0" data-testid="pdapp-setup-key-done">
                      <span className="inline-flex items-center gap-1 themed-chip-ok shrink-0"><Check size={12} />{t('appEnv.pickDone')}</span>
                      {shownKey && <ChosenKey view={shownKey} />}
                    </span>
                  ) : (
                    <span className="inline-flex items-start gap-1 themed-chip-warn min-w-0" data-testid="pdapp-setup-key-reason">
                      <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                      <span className="min-w-0">{reason(item.needs)}</span>
                    </span>
                  )}
                </div>
              </div>
              {!done && (
                <div className="flex items-center gap-1 shrink-0">
                  {!isOpen && (
                    <button
                      type="button"
                      onClick={() => openRow(item)}
                      disabled={disabled || busy}
                      className="inline-flex items-center gap-1 text-xs themed-btn-primary px-2.5 py-1 rounded disabled:opacity-40"
                      aria-label={t('appEnv.chooseKeyFor', { name: item.name })}
                      data-testid="pdapp-setup-key-choose"
                    ><KeyRound size={12} />{t('appEnv.chooseKey')}</button>
                  )}
                  {extraAction?.(item)}
                </div>
              )}
            </div>
            {isOpen && (
              <div className={framed ? 'mt-2.5 -mx-3 -mb-2.5 p-3 border-t themed-border themed-bg-secondary space-y-3' : 'mt-2 space-y-3'} data-testid="pdapp-setup-key-panel">
                {prefilling === item.contentHash ? (
                  <p className="text-xs themed-text-muted" data-testid="pdapp-setup-key-prefilling"><Loader2 size={12} className="inline animate-spin mr-1" />{t('appEnv.loadingKeys')}</p>
                ) : (
                  <KeyFieldPicker value={choice} onChange={setChoice} idPrefix="pdapp-setup" onPickedKey={setPickedKey} fallback={prefillFallback && choice.entryId === prefillFallback.entryId ? prefillFallback : undefined} />
                )}
                {error && <p className="text-xs text-red-400" role="alert">{error}</p>}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => closeRow(item)}
                    disabled={busy}
                    className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded themed-text-muted hover:themed-text"
                    data-testid="pdapp-setup-key-cancel"
                  ><X size={13} />{t('appEnv.cancel')}</button>
                  <button
                    type="button"
                    onClick={() => {
                      // Display only: remember how this key is shown, for the row (and the host's review) once it is in.
                      if (choice.entryId && choice.field) {
                        const view: ChosenKeyView = { label: pickedKey?.label ?? null, credentialType: pickedKey?.credentialType ?? '', field: choice.field }
                        setUsedKeys(prev => new Map(prev).set(item.contentHash, view))
                        onUse?.(item.contentHash, view)
                      }
                      focusNext.current = { kind: 'after-use', hash: item.contentHash }
                      void save(item)
                    }}
                    disabled={disabled || busy || !choice.entryId || !choice.field}
                    className="inline-flex items-center gap-1.5 text-sm themed-btn-primary px-3 py-1.5 rounded disabled:opacity-40"
                    data-testid="pdapp-setup-key-use"
                  >{busy && <Loader2 size={13} className="animate-spin" />}{t('appEnv.useKey')}</button>
                </div>
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** The key step's heading and how many of its secrets have a key so far. */
export function AppKeysStepIntro({ total, chosen }: { total: number; chosen: number }) {
  const { t } = useTranslation('keybook')
  return (
    <>
      <div className="text-center mb-4">
        <KeyRound size={26} className="mx-auto themed-icon mb-2" />
        <h2 className="text-base font-semibold themed-text">{t('appEnv.keysStepTitle')}</h2>
        <p className="text-xs themed-text-muted mt-1">{t('appEnv.keysStepIntro')}</p>
      </div>
      <div className="flex items-center justify-end mb-1.5 text-[11px] themed-text-muted tabular-nums" aria-live="polite" data-testid="pdapp-setup-keys-count">
        {t('appEnv.keysStepCount', { chosen, total })}
      </div>
    </>
  )
}

/** A key pick on the setup's review page: its key chip once chosen, else "Set aside". */
export function AppKeyPickSummary({ name, done, chosenKey }: { name: string; done: boolean; chosenKey?: ChosenKeyView }) {
  const { t } = useTranslation('keybook')
  return (
    <li className="flex items-center gap-2 px-3 py-2 rounded themed-bg-secondary" data-testid="pdapp-setup-key-summary" data-name={name}>
      {done ? <Check size={14} className="themed-chip-ok shrink-0" /> : <X size={14} className="themed-text-muted shrink-0" />}
      <span className="flex items-center gap-2 min-w-0 flex-1 text-xs">
        <code className={`text-sm font-mono truncate ${done ? 'themed-text' : 'themed-text-muted'}`} title={name}>{name}</code>
        {done && chosenKey && <ChosenKey view={chosenKey} />}
      </span>
      <span className={`text-[10px] uppercase tracking-wide shrink-0 ${done ? 'themed-chip-ok' : 'themed-text-muted'}`}>{done ? t('appEnv.pickDone') : t('appEnv.setAside')}</span>
    </li>
  )
}
